import React, {useEffect, useState} from 'react';
import Box from "@mui/material/Box";
import {
    Button,
    FormControl,
    FormLabel,
    Grid,
    MenuItem,
    Select,
    Typography,
} from "@mui/material";
import {useDispatch, useSelector} from "react-redux";
import {useParams} from "react-router-dom";
import {getStaff} from "../../../../../store/crm/staffs";
import {storeCrmNotes} from "../../../../../store/crm/crm/crm-details";
import Waveloading from '../../../../loading/Waveloading';
import toast from 'react-hot-toast';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import MedicalServicesOutlinedIcon from '@mui/icons-material/MedicalServicesOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { NOTE_MAX_LENGTH } from "../../../../../utils/noteLimits";
import NoteLimitCounter from "../../../../../components/note-limit/NoteLimitCounter";

const BURGUNDY = '#A72C68';
const BURGUNDY_DARK = '#921F58';
const PINK_BG = '#FAEFF5';
const PINK_BORDER = '#F0CDDB';
const PRIMARY_GRADIENT = `linear-gradient(135deg, ${BURGUNDY} 0%, ${BURGUNDY_DARK} 100%)`;

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

const labelStyles = {
    color: BURGUNDY,
    fontSize: '12px',
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    mb: '6px',
    "&.Mui-focused": {
        color: `${BURGUNDY}!important`,
    }
};

const TypePill = ({active, icon, label, onClick}) => (
    <Box
        onClick={onClick}
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1.75,
            py: 1,
            borderRadius: '10px',
            cursor: 'pointer',
            userSelect: 'none',
            transition: 'all 0.15s ease',
            background: active ? PRIMARY_GRADIENT : '#fff',
            color: active ? '#fff' : '#3b4252',
            border: active ? '1px solid transparent' : `1px solid ${PINK_BORDER}`,
            boxShadow: active ? '0 2px 6px rgba(167,44,104,0.25)' : 'none',
            flex: 1,
            justifyContent: 'center',
            '&:hover': {
                background: active ? 'linear-gradient(135deg,#921F58 0%,#7E1A4C 100%)' : PINK_BG,
            },
        }}
    >
        {React.cloneElement(icon, {sx: {fontSize: 16, color: active ? '#fff' : BURGUNDY}})}
        <Typography sx={{fontSize: '13px', fontWeight: 600, color: 'inherit'}}>{label}</Typography>
    </Box>
);

const CrmAddNotes = ({customerId, embedded = false, onSavingChange}) => {
    const [selectedStaff, setSelectedStaff] = useState({})
    const [loading, setLoading] = useState(false)
    const [staffLoading, setStaffLoading] = useState(false)
    const [comments, setComments] = useState('')
    const [type, setType] = useState('Medical')

    const dispatch = useDispatch()
    const {user_id: routeUserId} = useParams()

    // Rendered both on its own route and inside CustomerNotesDrawer, which has no
    // user_id in the URL — the prop wins there, the route param on the page.
    const user_id = customerId ?? routeUserId

    const crmStaffStore = useSelector(state => state?.crmStaffsStore)
    const {crmStaffs = []} = crmStaffStore

    useEffect(() => {
        (async () => {
            setStaffLoading(true)
            await dispatch(getStaff())
            setStaffLoading(false)
        })()
    }, [])

    useEffect(() => {
        if (crmStaffs && crmStaffs.length > 0) {
            setSelectedStaff(crmStaffs[0]);
        }
    }, [crmStaffs]);

    // Embedded in a drawer, the overlay belongs to the panel rather than this
    // section, so the save state is reported upwards as well as kept locally.
    const setSaving = (value) => {
        setLoading(value)
        if (onSavingChange) onSavingChange(value)
    }

    const handleSubmit = async () => {
        if (comments?.length > 3 && selectedStaff?.id) {

            let finalData = {
                customer_id: user_id,
                staff_id: selectedStaff?.id,
                type: type,
                description: comments
            }
            setSaving(true)

            let res = await dispatch(storeCrmNotes(finalData))
            setSaving(false)
            if (res?.payload?.success === 'true') {
                setType('Staff')
                setSelectedStaff({})
                setComments('')
                toast.success(`${type} Note added successfully! `)
            }
        }
    }
    return (
        <Box
            sx={embedded ? {
                background: '#fff',
                p: {sm: '16px', xs: '14px'},
                position: 'relative',
            } : {
                background: '#fff',
                borderRadius: '14px',
                border: `1px solid ${PINK_BORDER}`,
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                p: {sm: '20px', xs: '14px'},
                position: 'relative',
            }}
        >
            {/* In a drawer the panel draws its own scoped overlay — a fullSize
                viewport overlay would blank the whole screen behind the slider. */}
            {loading && !embedded ? <Waveloading fullSize/> : ''}
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.25, mb: 2.5}}>
                <Box
                    sx={{
                        width: 38,
                        height: 38,
                        borderRadius: '10px',
                        background: PINK_BG,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <StickyNote2OutlinedIcon sx={{color: BURGUNDY, fontSize: 22}}/>
                </Box>
                <Box>
                    <Typography sx={{fontSize: {xs: '15px', sm: '17px'}, fontWeight: 700, color: '#1a1a2e', lineHeight: 1.2, fontFamily: 'helvetica-lt-std-bold'}}>
                        Add New Notes
                    </Typography>
                    <Typography sx={{fontSize: '11px', color: '#6B7280', mt: '2px'}}>
                        Record a medical or staff note for this customer.
                    </Typography>
                </Box>
            </Box>
            <Grid container spacing={2.5}>
                <Grid item xs={12} xl={embedded ? 12 : 6}>
                    <FormControl fullWidth>
                        <FormLabel sx={{...labelStyles}}>Staff Name</FormLabel>
                        <Select
                            size={'small'}
                            value={selectedStaff?.id || ''}
                            displayEmpty
                            MenuProps={MenuProps}
                            renderValue={() => selectedStaff?.name || <Box component="span" sx={{color: '#9CA3AF'}}>Select staff</Box>}
                            sx={{
                                background: PINK_BG,
                                borderRadius: '10px',
                                '& .MuiOutlinedInput-notchedOutline': {borderColor: PINK_BORDER},
                                '&:hover .MuiOutlinedInput-notchedOutline': {borderColor: BURGUNDY},
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {borderColor: BURGUNDY},
                            }}
                        >
                            {staffLoading ?
                                <MenuItem value={""}>
                                    Please Wait. Loading...
                                </MenuItem> :
                                crmStaffs?.length > 0 && crmStaffs?.map((staff, key) => (
                                    <MenuItem
                                        key={key}
                                        value={staff?.id}
                                        onClick={() => setSelectedStaff(staff)}
                                    >
                                        {staff?.name}
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} xl={embedded ? 12 : 6}>
                    <FormControl fullWidth>
                        <FormLabel sx={{...labelStyles}}>Note Type</FormLabel>
                        <Box sx={{display: 'flex', gap: 1}}>
                            <TypePill
                                active={type === 'Medical'}
                                icon={<MedicalServicesOutlinedIcon/>}
                                label="Medical"
                                onClick={() => setType('Medical')}
                            />
                            <TypePill
                                active={type === 'Staff'}
                                icon={<PersonOutlineOutlinedIcon/>}
                                label="Staff"
                                onClick={() => setType('Staff')}
                            />
                        </Box>
                    </FormControl>
                </Grid>
                <Grid item xs={12}>
                    <FormControl fullWidth>
                        <FormLabel sx={{...labelStyles}}>Notes</FormLabel>
                        <Box sx={{
                            position: 'relative',
                            border: `1px solid ${PINK_BORDER}`,
                            borderRadius: '10px',
                            background: PINK_BG,
                            transition: 'border-color 0.15s',
                            '&:focus-within': {borderColor: BURGUNDY, boxShadow: '0 0 0 2px rgba(167,44,104,0.10)'},
                        }}>
                            <textarea
                                rows={4}
                                placeholder="Type your note here…"
                                maxLength={NOTE_MAX_LENGTH}
                                value={comments}
                                onChange={(e) => setComments(e.target.value)}
                                style={{
                                    width: '100%',
                                    resize: 'vertical',
                                    padding: '10px 12px 22px',
                                    border: 'none',
                                    outline: 'none',
                                    borderRadius: '10px',
                                    fontFamily: 'inherit',
                                    fontSize: '14px',
                                    color: '#1a1a2e',
                                    background: 'transparent',
                                    boxSizing: 'border-box',
                                }}
                            />
                            <NoteLimitCounter value={comments} />
                        </Box>
                    </FormControl>
                </Grid>
                <Grid xs={12} item>
                    <Button
                        onClick={handleSubmit}
                        startIcon={<SaveOutlinedIcon sx={{fontSize: '16px !important'}}/>}
                        sx={{
                            height: 44, px: 3,
                            borderRadius: '10px',
                            textTransform: 'capitalize',
                            fontSize: '14px',
                            fontWeight: 700,
                            color: '#fff',
                            background: 'linear-gradient(135deg, #27AE60 0%, #1E8449 100%)',
                            border: '1px solid #27AE60',
                            boxShadow: 'none',
                            '&:hover': {
                                background: '#219653',
                                color: '#fff',
                                border: '1px solid #27AE60',
                                boxShadow: 'none',
                            },
                        }}
                    >
                        Save Note
                    </Button>
                </Grid>
            </Grid>
        </Box>
    );
};

export default CrmAddNotes;
